import angular from 'angular';

export default function TaskCollection(
  Restangular,
  Authentication,
  Socket,
  PersonaCollection,
  $rootScope,
  $q,
  $sce,
  $sanitize
) {
  'ngInject';

  const service = this;
  let _promise = null;
  service._logCache = {};

  service.clearData = clearData;
  service.getLogs = getLogs;
  service.loadData = loadData;
  service.refresh = refresh;
  service.getTaskDetails = getTaskDetails;
  service.addOrEditTask = addOrEditTask;
  service.deleteTask = deleteTask;
  service.addOrEditComment = addOrEditComment;
  service.deleteComment = deleteComment;
  service.removeLogsFromCache = removeLogsFromCache;

  _clearData();
  initSocketCommunication();

  // PRIVATE

  function _clearData() {
    service.allTasks = null;
    service.myTasks = [];
    service.createdTasks = [];
    service.completedTasks = [];
    service._logCache = {};
  }

  function _isMyTask(task) {
    if (task.personas.find((p) => p.isMe)) {
      return true;
    }

    const me = PersonaCollection.me;
    return task.personas.some((p) => me.groupIds.includes(p.id));
  }

  function _filterTasks() {
    service.myTasks = [];
    service.createdTasks = [];
    service.completedTasks = [];

    service.allTasks.forEach((task) => {
      if (!task.completed && !task.deleted && _isMyTask(task)) {
        service.myTasks.push(task);
      }

      if (!task.completed && !task.deleted && !_isMyTask(task)) {
        service.createdTasks.push(task);
      }

      if (task.completed && !task.deleted) {
        service.completedTasks.push(task);
      }
    });
  }

  function _loadFromServer() {
    const promises = [
      Restangular.all('tasks').getList({ filter: 'assigned', 'user-fields': 'id', pagesize: 200 }),
      Restangular.all('tasks').getList({ filter: 'created', 'user-fields': 'id', pagesize: 200 }),
      Restangular.all('tasks').getList({ filter: 'completed', 'user-fields': 'id', pagesize: 200 }),
    ];

    return $q.all(promises).then((data) => {
      const uniqueTaskIds = {};
      service.allTasks = data
        .reduce((a, b) => a.concat(b), [])
        .filter((task) => {
          if (uniqueTaskIds[task.id]) {
            return false;
          }

          uniqueTaskIds[task.id] = task;
          return true;
        });

      _filterTasks();

      return true;
    });
  }

  // PUBLIC

  function clearData() {
    _promise = null; // by setting _promise to null we can call loadData again
    _clearData();
  }

  function loadData() {
    if (_promise !== null) {
      return _promise;
    }

    _clearData();

    Socket.send({
      command: 'join',
      ignore: Authentication.token,
      room: `tasks:${Authentication.user.org.id}`,
    });

    _promise = _loadFromServer();
    return _promise;
  }

  service.setDirty = function setDirty() {
    _promise = null;
  };

  function refresh() {
    if (!__TEST__) {
      console.log('refresh TaskCollection');
    }

    if (service.allTasks) {
      service.clearData();
      return service.loadData();
    }

    return $q.resolve();
  }

  function getTaskDetails(task) {
    if (task.detailsLoaded) {
      return $q.when(task);
    }

    return Restangular.one('tasks', task.id)
      .get({ 'user-fields': 'id' })
      .then((data) => {
        angular.extend(task, data);
        Restangular.restangularizeElement(null, task, 'tasks');
        task.detailsLoaded = true;
        return task;
      });
  }

  function addOrEditTask(task) {
    if (!__TEST__) {
      console.log('addOrEditTask', task);
    }

    if (service.allTasks) {
      const existingTask = service.allTasks.find((t) => t.id === task.id);

      if (existingTask) {
        // Update
        angular.extend(existingTask, task);
        Restangular.restangularizeElement(null, existingTask, 'tasks');
        service.removeLogsFromCache(task.id);
      } else {
        // Insert
        Restangular.restangularizeElement(null, task, 'tasks');
        service.allTasks.push(task);
      }

      _filterTasks();
    }
  }

  function deleteTask(task) {
    if (!__TEST__) {
      console.log('deleteTask', task);
    }

    if (service.allTasks) {
      service.allTasks = service.allTasks.filter((t) => t.id !== task.id);
      _filterTasks();

      service.removeLogsFromCache(task.id);
    }
  }

  function addOrEditComment(comment) {
    console.log('addOrEditComment (task)', comment);

    if (service.allTasks) {
      const task = service.allTasks.find((t) => t.id === comment.taskId);

      if (task) {
        const existingComment = task.commentlist.find((c) => c.id === comment.id);

        if (existingComment) {
          // Update
          angular.extend(existingComment, comment);
          Restangular.restangularizeElement(null, existingComment, 'comments');
        } else {
          // Insert
          Restangular.restangularizeElement(null, comment, 'comments');
          task.commentlist.push(comment);
          task.comments++;
          service.removeLogsFromCache(task.id);
        }
      }
    }
  }

  function deleteComment(data) {
    console.log('deleteComment (task)', data);

    if (service.allTasks) {
      const task = service.allTasks.find((t) => t.id === data.taskId);

      if (task) {
        task.commentlist = task.commentlist.filter((c) => c.id !== data.id);
        task.comments--;
      }
    }
  }

  // Real time socket updates
  function initSocketCommunication() {
    $rootScope.$on('socket:task:new', (event, data) => {
      console.log('task:new');
      service.addOrEditTask(data);
    });

    $rootScope.$on('socket:task:edit', (event, data) => {
      console.log('task:edit');
      service.addOrEditTask(data);
    });

    $rootScope.$on('socket:task:delete', (event, data) => {
      console.log('task:delete');
      service.deleteTask(data);
    });

    $rootScope.$on('socket:task-comment:new', (event, data) => {
      console.log('task-comment:new');
      service.addOrEditComment(data);
    });

    $rootScope.$on('socket:task-comment:edit', (event, data) => {
      console.log('task-comment:edit');
      service.addOrEditComment(data);
    });

    $rootScope.$on('socket:task-comment:delete', (event, data) => {
      console.log('task-comment:delete');
      service.deleteComment(data);
    });
  }

  async function getLogs(taskId) {
    if (service._logCache[taskId]) {
      return service._logCache[taskId];
    }

    const data = await Restangular.all(`/tasks/${taskId}/logs`).customGET();
    data.forEach(_extendLogsModel);

    service._logCache[taskId] = data;
    return data;
  }

  function removeLogsFromCache(taskId) {
    if (taskId in service._logCache) {
      delete service._logCache[taskId];
    }
  }

  function _extendLogsModel(task) {
    Restangular.restangularizeElement(null, task.user, 'users');

    if (!task.changes) {
      task.changes = [];
    }

    if (task.type === 'comment') {
      task.changes.push('Commented the task');
    } else if (task.type === 'assigned') {
      task.membersBefore.map(_restangularizePersona);
      task.membersAfter.map(_restangularizePersona);

      task.changes.push(_getAssignedMessage(task));
    } else {
      const [head, ...tail] = task.type;
      task.changes.push(`${head.toUpperCase()}${tail.join('')} the task`);
    }

    task.changes = task.changes.map((change) => $sce.trustAsHtml($sanitize(change)));
  }

  function _restangularizePersona(persona) {
    const type = persona.type === 'user' ? 'users' : 'groups';
    Restangular.restangularizeElement(null, persona, type);
  }

  function _getAssignedMessage({ membersBefore, membersAfter }) {
    const delimiter = Authentication.user.settings.showLastNameFirst ? '; ' : ', ';
    let action;
    if (!membersBefore.length) {
      action = 'Assigned the task';
    } else {
      action = 'Reassigned the task from ';
      const beforeNames = membersBefore.map((member) => member.fullname || member.name);

      action += beforeNames.join(delimiter);
    }

    const afterNames = membersAfter.map((member) => member.fullname || member.name);
    return `${action} to ${afterNames.join(delimiter)}`;
  }
}
